// Mission-world data: where each curriculum domain "lives" on the Philippine map.
//
// This is STORY + NAVIGATION data only. It references existing domain/unit IDs from
// src/core/curriculum.js and never stores progress. Positions are approximate,
// for storytelling — the map makes no claim of cartographic accuracy.

import { PROJ } from './geo.js';

/** lon/lat → map units (same projection the outline was generated with). */
export const project = (lon, lat) => [(lon - PROJ.lon0) * PROJ.kx, (PROJ.lat0 - lat) * PROJ.k];

export const COMPANY = 'RouteZero Technologies';

/** Island groups (label positions in lon/lat). */
export const REGIONS = [
  { id: 'luzon', name: 'LUZON', at: [121.75, 16.9] },
  { id: 'visayas', name: 'VISAYAS', at: [123.3, 11.75] },
  { id: 'mindanao', name: 'MINDANAO', at: [124.75, 8.05] },
];

/** Neutral water labels (no contested naming). */
export const WATERS = [
  { name: 'Philippine Sea', at: [125.2, 17.6] },
  { name: 'Sulu Sea', at: [121.0, 8.9] },
  { name: 'Celebes Sea', at: [124.0, 5.6] },
];

/**
 * One city per domain. `site` is the RouteZero facility (placed slightly inland so the
 * enterprise campus sits on land); `label` controls which side the world-view label sits
 * (Manila, Taguig and Clark are close together).
 */
export const CITIES = [
  {
    id: 'city-manila', domainId: 'd01', name: 'Manila', region: 'Luzon', at: [120.984, 14.599], label: 'left',
    role: 'Corporate Headquarters', description: 'RouteZero HQ — where every new engineer starts: onboarding network, service desk and first tickets.',
    site: { name: 'RouteZero HQ Tower', kind: 'hq', at: [121.0, 14.585] },
    districts: [['Port Area', -2.6, 1.6], ['Business District', 3.2, 2.6], ['Old Town', -1.2, -2.4], ['University Belt', 2.2, -3.4]],
  },
  {
    id: 'city-cebu', domainId: 'd02', name: 'Cebu City', region: 'Visayas', at: [123.894, 10.316], label: 'right',
    role: 'Corporate Campus', description: 'The Visayas campus: three floors of users on a switched distribution network — VLANs, trunks, STP and EtherChannel.',
    site: { name: 'RouteZero Cebu Campus', kind: 'campus', at: [123.872, 10.334] },
    districts: [['IT Park', -0.6, -2.6], ['Port Area', 2.6, 1.8], ['Uptown', -3.0, 0.4], ['Reclamation', 3.4, 3.6]],
  },
  {
    id: 'city-davao', domainId: 'd03', name: 'Davao City', region: 'Mindanao', at: [125.613, 7.073], label: 'right',
    role: 'Southern Regional Network', description: 'RouteZero Southern Regional Network: the routing core (R2) at the regional HQ ties Branch Office A, Branch Office B, the Regional DC and the ISP POP together — static, OSPF, EIGRP and eBGP.',
    site: { name: 'RouteZero Southern Regional HQ', kind: 'regional', at: [125.585, 7.095] },
    districts: [['Downtown', -0.6, 0.9], ['Harbour', -1.2, 3.4], ['Riverside', -3.6, -1.0], ['Hill District', -6.4, -3.4]],
    // City-level network overlay (presentation only). Facilities and links light up as
    // the mission named in `by` is completed; `incident` facilities/links show the
    // outage while the final challenge is open. Offsets are map units from `at`.
    network: {
      incidentUnit: 'u14', incidentLabel: 'SOUTHERN NETWORK ROUTING OUTAGE',
      facilities: [
        { id: 'hq', name: 'Southern Regional HQ', role: 'R2 · regional core', at: 'site', by: null, incident: true },
        { id: 'branch-a', name: 'Branch Office A', role: 'R1 · LAN 10.30.1.0/24', dx: -6.0, dy: -0.4, by: 'u02', incident: true },
        { id: 'branch-b', name: 'Branch Office B', role: 'R3 · LAN 10.30.2.0/24', dx: -4.6, dy: -5.0, by: 'u02', incident: true },
        { id: 'dc', name: 'Regional DC', role: 'SRV-DC · 10.30.10.0/24', dx: -5.0, dy: 2.4, by: 'u07' },
        { id: 'isp', name: 'ISP POP', role: 'AS 64500 · 198.51.100.0/30', dx: -0.6, dy: -4.6, by: 'u13', incident: true },
        { id: 'noc', name: 'Network Operations', role: 'Monitoring & change desk', dx: -1.9, dy: 0.5, by: 'u01' },
      ],
      links: [
        { a: 'hq', b: 'branch-a', label: 'R2–R1 · 10.30.12.0/30', by: 'u02', incident: true },
        { a: 'hq', b: 'branch-b', label: 'R2–R3 · 10.30.23.0/30', by: 'u02', incident: true },
        { a: 'branch-a', b: 'branch-b', label: 'Backup R1–R3 · 10.30.13.0/30', by: 'u04' },
        { a: 'hq', b: 'dc', label: 'R2 G0/3 · 10.30.10.0/24', by: 'u07' },
        { a: 'hq', b: 'isp', label: 'eBGP R2–ISP · 198.51.100.0/30', by: 'u13', incident: true },
        { a: 'noc', b: 'hq', label: 'Monitoring', by: 'u01' },
      ],
    },
  },
  {
    id: 'city-batangas', domainId: 'd04', name: 'Batangas City', region: 'Luzon / CALABARZON', at: [121.058, 13.756], label: 'right',
    role: 'Regional WAN Hub + Carrier Gateway', description: 'RouteZero\'s regional WAN aggregation point: leased lines to Manila HQ and the Lipa branch, a backup carrier circuit, the Internet edge, QoS for voice and IPsec VPNs to other offices.',
    site: { name: 'RouteZero Batangas Regional WAN Hub', kind: 'wanhub', at: [121.075, 13.775] },
    districts: [['Port District', -3.2, 2.4], ['Town Centre', 1.2, 0.6], ['Industrial Zone', 5.2, -3.4]],
    network: {
      incidentUnit: 'u14', incidentLabel: 'BATANGAS WAN OUTAGE',
      facilities: [
        { id: 'hub', name: 'Regional WAN Hub', role: 'R2 · WAN aggregation', at: 'site', by: null, incident: true },
        { id: 'manila', name: 'To Manila HQ', role: 'R1 · primary T1 + backup', dx: -1.4, dy: -5.4, by: 'u06', incident: true },
        { id: 'lipa', name: 'Lipa Branch', role: 'Point-to-point T1 tail', dx: 4.6, dy: -4.8, by: 'u03' },
        { id: 'carrier', name: 'Carrier POP / ISP', role: 'Internet + carrier hand-off', dx: -3.2, dy: -3.0, by: 'u04', incident: true },
        { id: 'alt', name: 'Alternate Edge', role: 'R3 · backup carrier circuit', dx: 5.4, dy: -0.4, by: 'u05', incident: true },
        { id: 'cebu', name: 'VPN to Cebu', role: 'IPsec over the Internet', dx: 6.6, dy: 3.4, by: 'u11', incident: true },
      ],
      links: [
        { a: 'hub', b: 'manila', label: 'Primary T1 172.16.12.0/30', by: 'u06', incident: true },
        { a: 'hub', b: 'lipa', label: 'T1 tail 172.16.24.0/30', by: 'u03' },
        { a: 'hub', b: 'carrier', label: 'Internet / carrier hand-off', by: 'u04', incident: true },
        { a: 'hub', b: 'alt', label: 'Campus 10.40.0.0/30', by: 'u05', incident: true },
        { a: 'alt', b: 'manila', label: 'Backup carrier Ethernet', by: 'u05', incident: true },
        { a: 'carrier', b: 'cebu', label: 'IPsec VTI 172.16.99.0/30', by: 'u11', incident: true },
      ],
    },
  },
  {
    id: 'city-clark', domainId: 'd05', name: 'Clark / Angeles', region: 'Luzon / Central Luzon', at: [120.588, 15.15], label: 'left',
    role: 'RouteZero Data Center · Infrastructure Services', description: 'RouteZero\'s data center in the Clark Freeport Zone: the shared infrastructure services every site depends on — DHCP and relay, DNS, NAT to the Internet, redundant gateways, time and monitoring.',
    site: { name: 'RouteZero Data Center', kind: 'datacenter', at: [120.56, 15.175] },
    districts: [['Freeport Zone', -2.4, -1.4], ['Angeles Centre', 2.4, 1.6], ['Airfield', -3.6, 2.2]],
    network: {
      incidentUnit: 'u14', incidentLabel: 'CLARK DATA CENTER SERVICES OUTAGE',
      facilities: [
        { id: 'core', name: 'Data Center Core', role: 'R1 · OSPF core', at: 'site', by: null, incident: true },
        { id: 'svc', name: 'Service Network', role: 'R2 · DHCP, DNS, NTP', dx: -4.6, dy: -2.6, by: 'u03', incident: true },
        { id: 'users', name: 'User Floor', role: 'R3 / R4 · HSRP VIP .1', dx: 4.4, dy: 1.8, by: 'u10', incident: true },
        { id: 'edge', name: 'Internet Edge', role: 'EDGE · NAT / PAT', dx: 2.2, dy: -4.8, by: 'u07', incident: true },
        { id: 'noc', name: 'Monitoring', role: 'SRV-MON · Syslog / SNMP', dx: -4.8, dy: 2.6, by: 'u13', incident: true },
        { id: 'partner', name: 'Partner Portal', role: 'Static NAT 198.51.100.34', dx: 5.6, dy: -2.6, by: 'u06' },
      ],
      links: [
        { a: 'core', b: 'svc', label: 'R1–R2 10.50.1.0/30 · DHCP relay', by: 'u03', incident: true },
        { a: 'core', b: 'users', label: 'R1–R3/R4 · user VLAN 10.50.10.0/24', by: 'u10', incident: true },
        { a: 'core', b: 'edge', label: 'R1–EDGE 10.50.0.0/30', by: 'u07', incident: true },
        { a: 'svc', b: 'noc', label: 'Syslog UDP 514 · SNMP', by: 'u13', incident: true },
        { a: 'edge', b: 'partner', label: 'Published portal (HTTPS)', by: 'u06' },
      ],
    },
  },
  {
    id: 'city-taguig', domainId: 'd06', name: 'Taguig', region: 'Luzon', at: [121.051, 14.52], label: 'right',
    role: 'Security Operations Center · Infrastructure Security', description: 'RouteZero\'s Security Operations Center: hardened device access, SSH and VTY restrictions, AAA with RADIUS and TACACS+, traffic ACLs, port security and security logging.',
    site: { name: 'RouteZero Taguig SOC', kind: 'soc', at: [121.052, 14.535] },
    districts: [['Global City', -0.6, -1.4], ['Lakeshore', 2.6, 2.4], ['Heritage Park', -2.0, 1.8]],
    network: {
      incidentUnit: 'u14', incidentLabel: 'TAGUIG SOC SECURITY AUDIT FAILURE',
      facilities: [
        { id: 'core', name: 'SOC Edge / Core', role: 'R1 · SSH v2, AAA, ACLs', at: 'site', by: null, incident: true },
        { id: 'users', name: 'Analyst Floor', role: 'SW1 · VLAN 10 · port security', dx: -4.4, dy: 2.2, by: 'u10', incident: true },
        { id: 'servers', name: 'Server Farm', role: 'SRV-AAA · FILE · LOG · APP', dx: 4.6, dy: 2.0, by: 'u07', incident: true },
        { id: 'mgmt', name: 'Management', role: 'ADMIN-PC 10.60.99.10', dx: 4.0, dy: -3.0, by: 'u04', incident: true },
        { id: 'isp', name: 'Internet Edge', role: 'ISP 203.0.113.16/30', dx: -3.8, dy: -3.4, by: 'u03' },
      ],
      links: [
        { a: 'core', b: 'users', label: 'G0/1 · USERS-IN in 10.60.10.0/24', by: 'u07', incident: true },
        { a: 'core', b: 'servers', label: 'G0/2 · ACL 20 out · RADIUS / TACACS+', by: 'u12', incident: true },
        { a: 'core', b: 'mgmt', label: 'G0/3 · SSH only (access-class 15)', by: 'u04', incident: true },
        { a: 'core', b: 'isp', label: 'G0/0 · no Telnet from the Internet', by: 'u03' },
      ],
    },
  },
  {
    id: 'city-iloilo', domainId: 'd07', name: 'Iloilo City', region: 'Visayas', at: [122.564, 10.72], label: 'left',
    role: 'Network Operations Center · Infrastructure Management', description: 'RouteZero\'s Network Operations Center: configuration backups and restores, IOS upgrades and boot recovery, CDP/LLDP discovery, NTP, syslog and SNMP monitoring, documentation and change control.',
    site: { name: 'RouteZero Iloilo NOC', kind: 'noc', at: [122.545, 10.735] },
    districts: [['Esplanade', 1.6, 0.8], ['City Proper', 3.0, 2.4], ['Business Park', -2.4, -1.6]],
    network: {
      incidentUnit: 'u14', incidentLabel: 'FAILED CHANGE WINDOW',
      facilities: [
        { id: 'core', name: 'NOC Core', role: 'R1 · IOS 15.9 · boot / config', at: 'site', by: null, incident: true },
        { id: 'ops', name: 'Operations Floor', role: 'SW1 · PC-OPS · lobby AP (LLDP)', dx: -4.4, dy: 2.2, by: 'u08' },
        { id: 'servers', name: 'NOC Servers', role: 'SRV-NTP · SRV-MON · SRV-TFTP', dx: 2.4, dy: 1.6, by: 'u04', incident: true },
        { id: 'branch', name: 'Guimaras Branch', role: 'R2 · 10.70.30.0/24 · voice VLAN', dx: 3.2, dy: -3.8, by: 'u02', incident: true },
        { id: 'isp', name: 'Internet Edge', role: 'ISP 203.0.113.32/30', dx: -3.8, dy: -3.4, by: 'u08' },
      ],
      links: [
        { a: 'core', b: 'servers', label: 'G0/2 · TFTP backups · syslog / SNMP / NTP', by: 'u09', incident: true },
        { a: 'core', b: 'branch', label: 'G0/3 · WAN 10.70.12.0/30', by: 'u04', incident: true },
        { a: 'core', b: 'ops', label: 'G0/1 · NOC LAN 10.70.10.0/24', by: 'u02' },
        { a: 'core', b: 'isp', label: 'G0/0 · no CDP towards the ISP', by: 'u08', incident: true },
      ],
    },
  },
];

/** Backbone links between facilities (Manila HQ is the hub). */
export const BACKBONE = [
  ['d01', 'd06'], ['d01', 'd05'], ['d01', 'd04'], ['d04', 'd07'], ['d07', 'd02'], ['d01', 'd02'], ['d02', 'd03'],
];

// ---------------------------------------------------------------- enterprise sites
// Site scenes are drawn in local coordinates (0..100 × 0..64) and scaled into a small box
// centred on the facility. Every site uses the same four mission "slots"; names change
// per facility so each city feels distinct.
export const SITE_BOX = { w: 3.2, h: 2.05 };

const SLOT_GEOM = {
  learn: { x: 6, y: 8, w: 26, h: 17 },
  lab: { x: 38, y: 5, w: 30, h: 21 },
  ops: { x: 74, y: 8, w: 21, h: 17 },
  incident: { x: 44, y: 38, w: 32, h: 18 },
};
const SITE_NAMES = {
  hq: { learn: 'Onboarding Academy', lab: 'HQ Tower — Floors 4–9', ops: 'IT Service Desk', incident: 'Reception Wing' },
  campus: { learn: 'Learning Hub', lab: 'Building A — Labs', ops: 'Building C — Distribution', incident: 'Building B — Engineering Floors' },
  regional: { learn: 'Regional Training Room', lab: 'Core Router Hall', ops: 'Regional Ops Desk', incident: 'Branch Edge Room' },
  branch: { learn: 'Branch Training Room', lab: 'Carrier Hotel', ops: 'WAN Edge Room', incident: 'Port Branch Office' },
  wanhub: { learn: 'WAN Training Room', lab: 'Carrier Meet-Me Room', ops: 'Regional WAN NOC', incident: 'WAN Edge Room' },
  datacenter: { learn: 'DC Visitor Centre', lab: 'Data Hall 1', ops: 'Services Operations', incident: 'Data Hall 2' },
  soc: { learn: 'Security Academy', lab: 'Security Lab', ops: 'SOC Floor', incident: 'Incident Room' },
  noc: { learn: 'NOC Training Room', lab: 'Monitoring Lab', ops: 'NOC Floor', incident: 'Change Bridge' },
};

/** Presentation kind of a unit (existing `kind` wins; otherwise derived from its type). */
export function missionKind(u) {
  if (u.kind) return u.kind;
  if (/final/i.test(u.type)) return 'CHALLENGE';
  if (/troubleshoot|incident/i.test(u.type)) return 'TROUBLESHOOTING';
  if (/lesson/i.test(u.type)) return 'LEARN';
  return 'LAB';
}
const KIND_SLOT = { LEARN: 'learn', LAB: 'lab', TROUBLESHOOTING: 'ops', CHALLENGE: 'incident' };

/** Buildings + mission marker positions for a domain's site, in local site units. */
export function siteLayout(city, domain) {
  const names = SITE_NAMES[city.site.kind] || SITE_NAMES.campus;
  const buildings = Object.entries(SLOT_GEOM).map(([slot, g]) => ({ slot, name: names[slot], ...g }));
  const bySlot = Object.fromEntries(buildings.map((b) => [b.slot, []]));
  domain.units.forEach((u, i) => bySlot[KIND_SLOT[missionKind(u)]].push({ u, i }));
  const markers = [];
  for (const b of buildings) {
    // markers sit on the building, in a small grid below the roof band ("rooms")
    const list = bySlot[b.slot];
    const n = list.length;
    const cols = Math.min(n, b.w >= 28 ? 4 : 3) || 1;
    const rows = Math.ceil(n / cols) || 1;
    list.forEach(({ u, i }, k) => {
      const r = Math.floor(k / cols); const inRow = Math.min(cols, n - r * cols); const c = k % cols;
      const x = b.x + (b.w * (c + 1)) / (inRow + 1);
      const y = b.y + 3.2 + ((b.h - 3.2) * (r + 1)) / (rows + 1);
      markers.push({ unitId: u.id, index: i, slot: b.slot, x, y });
    });
  }
  markers.sort((a, b) => a.index - b.index);
  return { buildings, markers };
}

/** Local site coordinates → map units. */
export function siteToMap(city, lx, ly) {
  const [fx, fy] = project(...city.site.at);
  return [fx - SITE_BOX.w / 2 + (lx / 100) * SITE_BOX.w, fy - SITE_BOX.h / 2 + (ly / 64) * SITE_BOX.h];
}

/** Facility position (map units) for a city's network overlay. */
export function facilityAt(city, f) {
  if (f.at === 'site') return project(...city.site.at);
  const [cx, cy] = project(...city.at);
  return [cx + f.dx, cy + f.dy];
}

export const cityById = (id) => CITIES.find((c) => c.domainId === id || c.id === id) || null;

/** Skill tags shown on mission briefings (matched against the unit title). */
const SKILLS = [
  [/vlan/i, 'VLANs'], [/trunk|802\.1q/i, '802.1Q trunks'], [/native|allowed/i, 'Allowed/native VLANs'], [/stp|spanning/i, 'Spanning tree'],
  [/etherchannel|lacp/i, 'EtherChannel'], [/router-on|inter-vlan/i, 'Inter-VLAN routing'], [/layer 3|svi/i, 'L3 switching'],
  [/ipv4|address/i, 'IPv4 addressing'], [/subnet/i, 'Subnetting'], [/ipv6/i, 'IPv6'], [/tcp|udp|port|protocol/i, 'Transport & ports'],
  [/ethernet|mac/i, 'Ethernet & MAC'], [/cabl|physical/i, 'Cabling'], [/osi|tcp\/ip model/i, 'OSI / TCP-IP'], [/connectivity|ping/i, 'Connectivity testing'],
  [/method|troubleshoot/i, 'Troubleshooting method'], [/route|routing|ospf|eigrp|bgp|rip/i, 'Routing'], [/wan|vpn|qos/i, 'WAN'],
  [/dhcp|dns|nat|hsrp|vrrp|ntp|snmp|syslog/i, 'Infrastructure services'], [/acl|aaa|ssh|security|harden/i, 'Security'],
  [/backup|upgrade|cdp|lldp|monitor|ios/i, 'Operations'], [/final/i, 'Incident response'],
];
export function skillsFor(u, domain) {
  const s = SKILLS.filter(([re]) => re.test(u.title)).map(([, t]) => t);
  return [...new Set(s.length ? s : [domain.title])].slice(0, 4);
}
